<template>
  <div class="musubi-page musubi-page--campaigns">
    <header class="musubi-page__head">
      <h1>Campaigns</h1>
      <p class="text-muted">
        One-off marketing emails. A campaign pairs a template, a sender, and
        an audience (list or segment) into one launchable send —
        <strong>Launch</strong> works out who should get it and starts
        sending. Yorlin sends the rest automatically; watch progress here as
        it moves from draft to sending to sent.
      </p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <!-- Cross-link — a pipeline built in the wizard is a DRIP FLOW, not a
         campaign, and lands under Drip flows. Shown only when the tenant has at
         least one drip flow, so people who "started a campaign" in the wizard and
         came here looking for it get pointed to the right surface. -->
    <div v-if="hasDripFlows" class="wizard-xlink">
      <CygnusIcon name="diagram" class="wizard-xlink__ic" />
      <span>
        Looking for a pipeline you built in the wizard? Those run as automated sequences under
        <a href="/outreach/drip-flows">Drip flows</a> - this page is one-off list broadcasts.
      </span>
    </div>

    <!-- Toolbar -->
    <section class="actions-bar">
      <button class="btn btn-outline-primary" @click="toggleCreate">
        <CygnusIcon name="plus" class="me-1" />{{ showCreateForm ? 'Cancel' : 'New campaign' }}
      </button>
      <button class="btn btn-link" :disabled="busy.load" @click="refresh">
        <CygnusIcon name="spinner" :spin="true" v-if="busy.load" />
        <span v-else><CygnusIcon name="refresh" /> Refresh</span>
      </button>
    </section>

    <!-- Create / edit form (collapsible) -->
    <section v-if="showCreateForm" class="create-form card mb-3">
      <div class="card-body">
        <h6 class="mb-3">{{ editingId ? `Edit campaign #${editingId}` : 'New campaign' }}</h6>
        <form @submit.prevent="saveCampaign">
          <div class="row g-2">
            <div class="col-md-6">
              <label class="form-label small text-muted">Name</label>
              <input v-model="newCampaign.name" type="text" required class="form-control form-control-sm" placeholder="Q3 product launch">
            </div>
            <div class="col-md-6">
              <label class="form-label small text-muted">Subject {{ newCampaign.subject_b ? '(variant A)' : 'override (optional)' }}</label>
              <input
                ref="subjectInput"
                v-model="newCampaign.subject_override"
                type="text"
                class="form-control form-control-sm"
                placeholder="(uses template title if blank)"
              >
              <MergeTokenPicker dense class="mt-1" @insert="insertSubjectToken" />
            </div>
            <div class="col-md-6">
              <label class="form-label small text-muted">Subject B — A/B test (optional)</label>
              <input v-model="newCampaign.subject_b" type="text" class="form-control form-control-sm" placeholder="(leave blank for no A/B test)">
              <div class="form-text small">If set, recipients are split ~50/50 and each arm's open/click rate is reported.</div>
            </div>
            <div class="col-md-4">
              <label class="form-label small text-muted">Template {{ newCampaign.template_b_id ? '(variant A)' : '*' }}</label>
              <MusubiEntitySelect
                v-model="newCampaign.template_id"
                endpoint="/api/v1/branding/templates" response-key="templates"
                :option-label="t => t.title" :filter="t => t.surface === 'email'"
                placeholder="Select email template…" required />
              <div class="form-text small">Edit email templates under <a href="/admin/branding" target="_blank">Settings → Branding → Templates</a></div>
              <div class="form-text small">Each email carries a one-click unsubscribe link for its recipient. If the template has no {unsubscribe_url}, a short unsubscribe line is added at the end of each email.</div>
            </div>
            <div class="col-md-4">
              <label class="form-label small text-muted">Template B — content A/B (optional)</label>
              <MusubiEntitySelect
                v-model="newCampaign.template_b_id"
                endpoint="/api/v1/branding/templates" response-key="templates"
                :option-label="t => t.title" :filter="t => t.surface === 'email'"
                placeholder="(blank = no content test)" />
              <div class="form-text small">If set, arm B renders from this template (body/design split test).</div>
            </div>
            <div class="col-md-4">
              <label class="form-label small text-muted">Sender identity *</label>
              <MusubiEntitySelect
                v-model="newCampaign.sender_identity_id"
                endpoint="/api/v1/admin/senders"
                :option-label="s => s.label || `${s.from_name} <${s.from_email}>`"
                placeholder="Select sender…" required />
            </div>
            <div class="col-md-4">
              <label class="form-label small text-muted">List *</label>
              <MusubiEntitySelect
                v-model="newCampaign.list_id"
                endpoint="/api/v1/admin/lists"
                :option-label="l => l.name"
                placeholder="Select list…" required />
            </div>
            <div class="col-md-4">
              <label class="form-label small text-muted">Segment (optional)</label>
              <MusubiEntitySelect
                v-model="newCampaign.segment_id"
                endpoint="/api/v1/admin/segments"
                :option-label="s => s.name"
                placeholder="Whole list" />
            </div>
            <div class="col-md-4">
              <label class="form-label small text-muted">Schedule send (optional)</label>
              <input v-model="newCampaign.scheduled_at_local" type="datetime-local" class="form-control form-control-sm">
              <div class="form-text small">Leave blank to keep as a draft.</div>
            </div>
            <div class="col-md-4">
              <label class="form-label small text-muted">Description (optional)</label>
              <input v-model="newCampaign.description" type="text" class="form-control form-control-sm">
            </div>
            <div class="col-12">
              <label class="form-label small text-muted">Send window (optional)</label>
              <SendWindowEditor
                v-model="newCampaign.send_window"
                label="Override this campaign's send window"
                inherited-label="workspace window"
                :inherited-summary="workspaceWindowSummary"
                :default-timezone="workspaceTimezone"
              />
              <div class="form-text small">When off, this campaign inherits the workspace-wide send window. Override it to send only within specific days and times.</div>
            </div>
            <div class="col-12">
              <label class="form-label small text-muted">Send-time optimisation</label>
              <SendTimingToggle v-model="newCampaign.send_timing" :entitled="stoEntitled" />
            </div>
          </div>
          <div class="d-flex gap-2 mt-3">
            <button type="submit" class="btn btn-primary btn-sm" :disabled="busy.create">
              <CygnusIcon name="spinner" :spin="true" v-if="busy.create" />
              <span v-else>{{ editingId ? 'Save changes' : (newCampaign.scheduled_at_local ? 'Schedule' : 'Save as draft') }}</span>
            </button>
          </div>
        </form>
      </div>
    </section>

    <!-- Detail panel for selected campaign -->
    <section v-if="selected" class="campaign-detail card mb-3">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h5 class="mb-1">
              {{ selected.campaign.name }}
              <span :class="['status-pill', `status-pill--${selected.campaign.status}`]">{{ selected.campaign.status }}</span>
            </h5>
            <p v-if="selected.campaign.description" class="text-muted small mb-0">{{ selected.campaign.description }}</p>
          </div>
          <button class="btn btn-link btn-sm" @click="selected = null">Close</button>
        </div>

        <!-- PECR-checked badge + checks (Growth Feature 5) -->
        <div v-if="pecr" class="pecr-panel">
          <div class="pecr-panel__head">
            <span class="pecr-badge" :class="{ fail: pecr.passed < pecr.total }">
              <CygnusIcon :name="pecr.passed < pecr.total ? 'warning' : 'shield'" />
              {{ pecr.passed < pecr.total ? `${pecr.passed}/${pecr.total} · needs attention` : `PECR-checked · ${pecr.passed}/${pecr.total}` }}
            </span>
            <span class="pecr-mode small text-muted">{{ pecr.badge_mode === 'blocking' ? 'Blocking mode — failing checks stop the send' : 'Advisory mode — send is never blocked' }}</span>
            <button class="btn btn-link btn-sm ms-auto p-0" @click="showChecks = !showChecks">{{ showChecks ? 'Hide checks' : 'Show checks' }}</button>
          </div>
          <p class="pecr-note small text-muted mb-0">These are mechanical checks, not a legal verdict. A passing badge means the safeguards below are in place — it doesn't certify that the campaign is lawful.</p>
          <ul v-if="showChecks" class="pecr-checks">
            <li v-for="c in pecr.checks" :key="c.key" :class="{ 'is-fail': c.counted && !c.pass, 'is-gated': c.gated }">
              <span class="chk__ic" :class="c.gated ? 'gated' : (c.pass ? 'pass' : 'fail')">
                <CygnusIcon :name="c.gated ? 'lock' : (c.pass ? 'check' : 'warning')" />
              </span>
              <span class="chk__body">
                <span class="chk__t">{{ c.label }}<span v-if="!c.counted" class="chk__gated-tag">not counted</span></span>
                <span class="chk__m">{{ c.detail }}</span>
                <a v-if="c.fix_route" :href="c.fix_route" class="chk__fix">Fix this <CygnusIcon name="arrow-right" /></a>
                <a v-else-if="c.upgrade_route" :href="c.upgrade_route" class="chk__fix">Available on Growth+ <CygnusIcon name="arrow-right" /></a>
              </span>
            </li>
          </ul>
        </div>

        <dl class="row small mb-3">
          <dt class="col-sm-3">Template</dt>     <dd class="col-sm-3" :title="'#' + selected.campaign.template_id">{{ templateName(selected.campaign.template_id) }}</dd>
          <dt class="col-sm-3">Sender</dt>       <dd class="col-sm-3" :title="'#' + selected.campaign.sender_identity_id">{{ senderName(selected.campaign.sender_identity_id) }}</dd>
          <dt class="col-sm-3">List</dt>         <dd class="col-sm-3" :title="'#' + selected.campaign.list_id">{{ listName(selected.campaign.list_id) }}</dd>
          <dt class="col-sm-3">Segment</dt>      <dd class="col-sm-3" :title="selected.campaign.segment_id ? ('#' + selected.campaign.segment_id) : ''">{{ segmentName(selected.campaign.segment_id) }}</dd>
          <dt class="col-sm-3">Recipients</dt>   <dd class="col-sm-3">{{ selected.campaign.total_recipients }}</dd>
          <dt class="col-sm-3">Started</dt>      <dd class="col-sm-3">{{ fmtDate(selected.campaign.started_at) }}</dd>
        </dl>

        <!-- W1 — the window a send from this campaign will actually respect -->
        <p v-if="effectiveWindowText" class="send-window-line small">
          <CygnusIcon name="clock" class="me-1" />{{ effectiveWindowText }}
        </p>
        <!-- W2 — send-time mode (optimise = per-contact best time within the window) -->
        <p v-if="selected.campaign.send_timing === 'optimise'" class="send-window-line small">
          <CygnusIcon name="bolt" class="me-1" />Send-time optimisation: each contact is sent at their learned best time within the send window.
        </p>

        <h6 class="text-uppercase text-muted small fw-bold mb-2">Delivery stats</h6>
        <div class="stat-tiles">
          <div v-for="b in detailBuckets" :key="b.key" :class="['stat-tile', `stat-tile--${b.tone}`]" :title="b.title">
            <div class="stat-tile__value">{{ selected.stats[b.key] ?? 0 }}</div>
            <div class="stat-tile__label">{{ b.label }}</div>
          </div>
        </div>

        <!-- A/B test results (subject and/or content) -->
        <div v-if="selected.variants && selected.variants.length" class="ab-results mt-3">
          <h6 class="text-uppercase text-muted small fw-bold mb-2">A/B test results</h6>
          <table class="table table-sm align-middle mb-0">
            <thead>
              <tr><th>Arm</th><th>Subject</th><th class="text-end">Template</th><th class="text-end">Recipients</th><th class="text-end">Opens</th><th class="text-end">Clicks</th><th class="text-end">Open %</th><th class="text-end">Click %</th></tr>
            </thead>
            <tbody>
              <tr v-for="v in selected.variants" :key="v.variant">
                <td><span class="ab-arm">{{ v.variant }}</span></td>
                <td class="small">{{ v.subject || '(template default)' }}</td>
                <td class="text-end small" :title="'#' + v.template_id">{{ templateName(v.template_id) }}</td>
                <td class="text-end">{{ v.recipients }}</td>
                <td class="text-end">{{ v.opens }}</td>
                <td class="text-end">{{ v.clicks }}</td>
                <td class="text-end" :class="{ 'fw-bold text-success': isWinner('open_rate', v) }">{{ v.open_rate }}%</td>
                <td class="text-end" :class="{ 'fw-bold text-success': isWinner('click_rate', v) }">{{ v.click_rate }}%</td>
              </tr>
            </tbody>
          </table>
          <div v-if="selected.significance" class="ab-sig small mt-2">
            <span class="ab-sig__metric">Opens:</span> <span v-html="sigLabel(selected.significance.opens)"></span>
            <span class="ab-sig__sep">·</span>
            <span class="ab-sig__metric">Clicks:</span> <span v-html="sigLabel(selected.significance.clicks)"></span>
          </div>
          <p class="small text-muted mt-1 mb-0">Significance is a two-proportion z-test; ≥95% confidence is called a winner.</p>
        </div>

        <!-- Recipients drill-down — who was sent to + how each interacted -->
        <div class="recipients mt-3">
          <h6 class="text-uppercase text-muted small fw-bold mb-2">Recipients</h6>
          <RecipientDrillDown :endpoint="`/api/v1/admin/campaigns/${selected.campaign.id}/recipients`" />
        </div>

        <!-- Top clicked links -->
        <div v-if="selected.top_links && selected.top_links.length" class="top-links mt-3">
          <h6 class="text-uppercase text-muted small fw-bold mb-2">Top clicked links</h6>
          <table class="table table-sm align-middle mb-0">
            <thead><tr><th>Link</th><th class="text-end">Clicks</th><th class="text-end">Unique</th></tr></thead>
            <tbody>
              <tr v-for="l in selected.top_links" :key="l.url">
                <td class="text-truncate" style="max-width:420px"><a :href="safeHref(l.url)" target="_blank" rel="noopener">{{ l.url }}</a></td>
                <td class="text-end">{{ l.clicks }}</td>
                <td class="text-end">{{ l.unique_clicks }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Send safety slice 6: who a launch would reach, by company registration status -->
        <div v-if="audience && canLaunch(selected.campaign)" class="audience-panel mt-3">
          <h6 class="text-uppercase text-muted small fw-bold mb-2">Who this campaign will reach</h6>
          <p class="small mb-2">{{ audience.total }} recipient{{ audience.total === 1 ? '' : 's' }} on the list or segment today.</p>
          <ul class="audience-counts small mb-2">
            <li v-for="row in audienceRows" :key="row.key" :class="{ 'is-skip': row.skip }">
              <span class="audience-counts__n">{{ row.n }}</span> {{ row.label }}<span v-if="row.skip" class="audience-counts__tag">skipped</span>
            </li>
          </ul>
          <p class="small text-muted mb-0">{{ audienceNote }}</p>
        </div>

        <div class="campaign-actions mt-3 pt-3 border-top d-flex gap-2 flex-wrap">
          <button
            v-if="canLaunch(selected.campaign)"
            class="btn btn-primary btn-sm"
            :disabled="busy.launch || busy.transition"
            @click="launch(selected.campaign.id)"
          >
            <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.launch" />
            <CygnusIcon name="rocket" class="me-1" v-else />
            Launch campaign
          </button>
          <button
            v-if="['draft', 'scheduled'].includes(selected.campaign.status)"
            class="btn btn-outline-secondary btn-sm"
            :disabled="busy.transition"
            @click="openEdit(selected.campaign)"
          >
            <CygnusIcon name="pen" class="me-1" />Edit
          </button>
          <button
            v-if="selected.campaign.status === 'sending'"
            class="btn btn-outline-warning btn-sm"
            :disabled="busy.transition"
            @click="transition(selected.campaign.id, 'pause')"
          >
            <CygnusIcon name="pause" class="me-1" />Pause
          </button>
          <button
            v-if="selected.campaign.status === 'paused'"
            class="btn btn-outline-primary btn-sm"
            :disabled="busy.transition"
            @click="transition(selected.campaign.id, 'resume')"
          >
            <CygnusIcon name="play" class="me-1" />Resume
          </button>
          <button
            v-if="['scheduled', 'sending', 'paused'].includes(selected.campaign.status)"
            class="btn btn-outline-danger btn-sm"
            :disabled="busy.transition"
            @click="transition(selected.campaign.id, 'cancel')"
          >
            <CygnusIcon name="x-circle" class="me-1" />Cancel
          </button>
          <button
            v-if="['sending', 'sent'].includes(selected.campaign.status)"
            class="btn btn-outline-secondary btn-sm"
            :disabled="busy.resend"
            @click="toggleResendForm"
          >
            <CygnusIcon name="envelope" class="me-1" />Resend to non-openers
          </button>
          <button
            v-if="selected.campaign.status === 'draft'"
            class="btn btn-outline-danger btn-sm ms-auto"
            :disabled="busy.transition"
            @click="deleteCampaign(selected.campaign.id)"
          >
            <CygnusIcon name="trash" class="me-1" />Delete draft
          </button>
        </div>
        <p v-if="lastLaunchMessage" :class="['mt-2 mb-0 small', launchOk ? 'text-success' : 'text-danger']">
          {{ lastLaunchMessage }}
        </p>

        <!-- Resend to non-openers: subject override + confirm -->
        <div v-if="resendForm.show" class="inline-panel mt-2">
          <h6 class="text-uppercase text-muted small fw-bold mb-2">Resend to non-openers</h6>
          <form class="d-flex gap-2 align-items-start flex-wrap" @submit.prevent="resendNonOpeners">
            <input
              v-model.trim="resendForm.subject"
              type="text"
              class="form-control form-control-sm resend-form__subject"
              placeholder="New subject (optional — reuses current subject if blank)"
              :disabled="busy.resend"
            >
            <button type="submit" class="btn btn-outline-secondary btn-sm" :disabled="busy.resend">
              <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.resend" />
              <span v-else>Queue resend</span>
            </button>
            <button type="button" class="btn btn-link btn-sm" :disabled="busy.resend" @click="resendForm.show = false">Cancel</button>
          </form>
        </div>
        <p v-if="resendMessage" :class="['mt-2 mb-0 small', resendOk ? 'text-success' : 'text-danger']">
          {{ resendMessage }}
        </p>

        <!-- Test send: preview render to one address, any status with a template -->
        <div v-if="selected.campaign.template_id" class="inline-panel mt-3">
          <h6 class="text-uppercase text-muted small fw-bold mb-2">Send a test</h6>
          <p class="text-muted small mb-2 test-send-form__help">
            Tests go only to your own team: your own address, another member of this
            workspace, or an address on one of its verified sending domains. Each
            workspace can send a limited number of tests an hour. A test does not
            include the unsubscribe line added to emails without {unsubscribe_url}.
          </p>
          <form class="d-flex gap-2 align-items-start flex-wrap" @submit.prevent="sendTest">
            <input
              v-model.trim="testSendEmail"
              type="email"
              class="form-control form-control-sm test-send-form__email"
              placeholder="you@yourcompany.com"
              :disabled="busy.testSend"
            >
            <button type="submit" class="btn btn-outline-primary btn-sm" :disabled="busy.testSend || !isValidEmail(testSendEmail)">
              <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.testSend" />
              <span v-else>Send test</span>
            </button>
          </form>
          <p v-if="testSendMessage" :class="['mt-2 mb-0 small', testSendOk ? 'text-success' : 'text-danger']">
            {{ testSendMessage }}
          </p>
        </div>
      </div>
    </section>

    <!-- Tabulator list -->
    <MusubiTabulator
      :rows="rows"
      :columns="columns"
      storage-key="admin.campaigns"
      :initial-sort="{ column: 'created', dir: 'desc' }"
      :page-size="25"
      :loading="busy.load"
      :error="error"
      @row-click="onRowClick"
    />
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiTabulator from "@root/components/MusubiTabulator.vue";
import MusubiEntitySelect from "@root/components/MusubiEntitySelect.vue";
import RecipientDrillDown from "@root/components/ui/RecipientDrillDown.vue";
import SendWindowEditor from "@root/components/SendWindowEditor.vue";
import SendTimingToggle from "@root/components/SendTimingToggle.vue";
import { esc, safeUrl } from "./util/cellHtml";
import { summariseWindow } from "./util/sendWindowSummary";

export default {
  name: 'AdminCampaigns',
  components: { MusubiTabulator, MusubiEntitySelect, RecipientDrillDown, SendWindowEditor, SendTimingToggle },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      rows: [],
      // Whether the tenant has any drip flows — gates the "pipelines live under
      // Drip flows" cross-link (cheap count off the existing list endpoint).
      hasDripFlows: false,
      selected: null,      // { campaign, stats }
      pecr: null,          // PECR check result for the selected campaign
      audience: null,      // send safety slice 6: recipients by company registration status
      showChecks: false,
      busy: { load: false, create: false, launch: false, transition: false, testSend: false, resend: false },
      error: null,
      lastLaunchMessage: '',
      launchOk: false,
      testSendEmail: '',
      testSendMessage: '',
      testSendOk: false,
      resendForm: { show: false, subject: '' },
      resendMessage: '',
      resendOk: false,
      showCreateForm: false,
      editingId: null,     // null = create mode; id = editing that campaign
      newCampaign: this.blankCampaign(),
      // id → display name lookups, loaded once so the table/detail can show
      // "Welcome template" / "Primary sender" instead of raw "#2" / "#1".
      templatesById: {},
      sendersById: {},
      listsById: {},
      segmentsById: {},
      // W1 — workspace send window (for the "inherits" hint + editor default tz).
      workspaceWindowSummary: '',
      workspaceTimezone: 'Europe/London',
      // W2 — send-time optimisation entitlement + tenant default (send-settings).
      stoEntitled: false,
      stoDefault: 'window',
    };
  },
  computed: {
    // Send safety slice 6: one line per status of the audience preview.
    audienceRows() {
      const a = this.audience;
      if (!a || !a.counts) return [];
      const skip = !!a.enforced;
      return [
        { key: 'consent',       label: 'signed up with consent (always sent)', n: a.counts.consent || 0,       skip: false },
        { key: 'registered',    label: 'at registered companies',              n: a.counts.registered || 0,    skip: false },
        { key: 'public_sector', label: 'at public sector bodies',               n: a.counts.public_sector || 0, skip: false },
        { key: 'unregistered',  label: 'at companies recorded as not registered', n: a.counts.unregistered || 0, skip },
        { key: 'unchecked',     label: 'at companies not checked yet',          n: a.counts.unchecked || 0,     skip },
      ];
    },
    audienceNote() {
      const a = this.audience;
      if (!a) return '';
      if (!a.enforced) {
        return a.switch_off
          ? 'The registered-companies check is switched off on this platform at the moment, so every recipient gets the campaign.'
          : 'This workspace sends cold email to any company, so every recipient gets the campaign.';
      }
      return `This workspace sends cold email only to registered companies and public sector bodies. ${a.will_skip} recipient${a.will_skip === 1 ? '' : 's'} will be skipped at launch, and each email is checked again just before it is sent.`;
    },
    // W1 honesty line for the selected campaign's detail panel.
    effectiveWindowText() {
      const ew = this.selected?.effective_window;
      if (!ew) return '';
      if (ew.source === 'none') return 'Send window: none — sends any time.';
      const summary = summariseWindow(ew.window);
      if (!summary) return '';
      return ew.source === 'campaign'
        ? `Send window (campaign override): ${summary.replace(/^Sends /, '')}`
        : `Send window (workspace): ${summary.replace(/^Sends /, '')}`;
    },
    columns() {
      const self = this;
      return [
        { key: 'id',               label: 'ID',         width: 60,  sorter: 'number' },
        { key: 'name',             label: 'Name' },
        { key: 'status',           label: 'Status',     width: 110,
          format: (v) => `<span class="status-pill status-pill--${esc(v)}">${esc(v)}</span>` },
        { key: 'total_recipients', label: 'Recipients', width: 120, sorter: 'number', hozAlign: 'right' },
        { key: 'list_id',          label: 'List',       width: 150, sorter: 'number',
          format: (v) => `<span title="#${esc(v)}">${esc(self.listName(v))}</span>` },
        { key: 'segment_id',       label: 'Segment',    width: 150, sorter: 'number',
          format: (v) => v
            ? `<span title="#${esc(v)}">${esc(self.segmentName(v))}</span>`
            : '<span class="text-muted">Whole list</span>' },
        { key: 'started_at',       label: 'Started',    width: 140, sorter: 'number',
          format: (v) => v ? new Date(v * 1000).toLocaleString() : '—' },
        { key: 'created',          label: 'Created',    width: 140, sorter: 'number',
          format: (v) => v ? new Date(v * 1000).toLocaleString() : '—' },
        { key: 'pecr',             label: 'PECR',       width: 110,
          format: (v) => self.pecrBadgeHtml(v) },
      ];
    },
    detailBuckets() {
      return [
        { key: 'queued',        label: 'Queued',    tone: 'wait'  },
        { key: 'sending',       label: 'Sending',   tone: 'wait'  },
        { key: 'sent',          label: 'Sent',      tone: 'ok'    },
        { key: 'delivered',     label: 'Delivered', tone: 'ok'    },
        { key: 'bounced',       label: 'Bounced',   tone: 'warn'  },
        { key: 'complained',    label: 'Complaint', tone: 'warn'  },
        { key: 'failed',        label: 'Failed',    tone: 'error' },
        { key: 'unique_opens',  label: 'Opens',     tone: 'engage' },
        { key: 'unique_clicks', label: 'Clicks',    tone: 'engage' },
      ];
    },
  },
  mounted() { this.refresh(); this.loadLookups(); this.loadWorkspaceWindow(); this.checkDripFlows(); },
  methods: {
    // Cheap existence check for the Drip flows cross-link. Best-effort: a 403
    // (no Outreach access) or any error just leaves the banner hidden.
    async checkDripFlows() {
      try {
        const res = await this.api.get('/api/v1/admin/drip-flows');
        const payload = res?.data?.data ?? res?.data ?? res;
        const list = Array.isArray(payload) ? payload : (payload?.rows || []);
        this.hasDripFlows = Array.isArray(list) && list.length > 0;
      } catch (e) { this.hasDripFlows = false; }
    },
    // W1 — fetch the workspace send window so the editor can show what an
    // un-overridden campaign inherits + seed the default timezone.
    async loadWorkspaceWindow() {
      try {
        const res = await this.api.get('/api/v1/admin/email/send-settings');
        const settings = (res?.data ?? res)?.settings || {};
        const sw = settings.send_window || {};
        // W2 — entitlement + tenant default send-time mode.
        const st = settings.send_timing || {};
        this.stoEntitled = !!st.entitled;
        this.stoDefault = st.default === 'optimise' ? 'optimise' : 'window';
        if (sw.timezone) this.workspaceTimezone = sw.timezone;
        if (sw.enabled && Array.isArray(sw.days) && sw.days.length) {
          this.workspaceWindowSummary = summariseWindow({
            days: sw.days,
            ranges: [{ start: (sw.start_hour || 0) * 60, end: (sw.end_hour || 0) * 60 }],
            timezone: sw.timezone || this.workspaceTimezone,
          });
        } else {
          this.workspaceWindowSummary = '';
        }
      } catch (e) { /* non-fatal — editor just shows the generic inherit hint */ }
    },
    // Sanitise a server-derived tracked-link URL before it lands in an href —
    // Vue attribute-escapes but does not neutralise javascript:/data: schemes.
    safeHref(url) { return safeUrl(url); },
    // Load the template/sender/list/segment collections once so the table
    // and detail panel can show names instead of raw ids (F-OU-03). Best
    // effort — a failed lookup just falls back to "#<id>" per name-getter.
    async loadLookups() {
      const unwrap = (res) => {
        const p = res?.data?.data ?? res?.data ?? res;
        return Array.isArray(p) ? p : (p?.rows || p?.data || []);
      };
      const [tRes, sRes, lRes, gRes] = await Promise.all([
        this.api.get('/api/v1/branding/templates').catch(() => null),
        this.api.get('/api/v1/admin/senders').catch(() => null),
        this.api.get('/api/v1/admin/lists').catch(() => null),
        this.api.get('/api/v1/admin/segments').catch(() => null),
      ]);
      if (tRes) {
        const tPayload = tRes?.data ?? tRes;
        const templates = Array.isArray(tPayload?.templates) ? tPayload.templates : unwrap(tRes);
        this.templatesById = Object.fromEntries(templates.map((t) => [t.id, t.title || t.name || `#${t.id}`]));
      }
      if (sRes) {
        const senders = unwrap(sRes);
        this.sendersById = Object.fromEntries(
          senders.map((s) => [s.id, s.label || (s.from_name ? `${s.from_name} <${s.from_email}>` : `#${s.id}`)])
        );
      }
      if (lRes) {
        const lists = unwrap(lRes);
        this.listsById = Object.fromEntries(lists.map((l) => [l.id, l.name || `#${l.id}`]));
      }
      if (gRes) {
        const segments = unwrap(gRes);
        this.segmentsById = Object.fromEntries(segments.map((s) => [s.id, s.name || `#${s.id}`]));
      }
    },
    // Fetch the PECR check result for the selected campaign (best-effort).
    async loadPecr(id) {
      try {
        const res = await this.api.get(`/api/v1/admin/campaigns/${id}/pecr`);
        this.pecr = res && res.checks !== undefined ? res : (res?.data ?? res);
      } catch (e) { this.pecr = null; }
    },
    // Send safety slice 6: the audience by company registration status (best-effort).
    async loadAudience(id) {
      try {
        const res = await this.api.get(`/api/v1/admin/campaigns/${id}/audience-preview`);
        const payload = res?.data ?? res;
        this.audience = payload && payload.counts ? payload : (payload?.data ?? null);
      } catch (e) { this.audience = null; }
    },
    // Small badge for the list column. row.pecr = {passed,total,entitled} | null.
    pecrBadgeHtml(p) {
      if (!p || typeof p.total !== 'number') return '<span class="text-muted">—</span>';
      const fail = p.passed < p.total;
      const cls = fail ? 'pecr-badge pecr-badge--sm fail' : 'pecr-badge pecr-badge--sm';
      const icon = fail ? '&#9888;' : '&#10003;';
      return `<span class="${cls}">${icon} ${p.passed}/${p.total}</span>`;
    },
    templateName(id) { return id ? (this.templatesById[id] || `#${id}`) : '—'; },
    senderName(id)   { return id ? (this.sendersById[id]   || `#${id}`) : '—'; },
    listName(id)     { return id ? (this.listsById[id]     || `#${id}`) : '—'; },
    // segment_id === null/0 is the "whole list" sentinel, not a missing name.
    segmentName(id)  { return id ? (this.segmentsById[id]  || `#${id}`) : 'Whole list'; },
    blankCampaign() {
      return {
        name: '', description: '', template_id: null, template_b_id: null, sender_identity_id: null,
        subject_override: '', subject_b: '', list_id: null, segment_id: null, scheduled_at_local: '',
        send_window: null,
        // W2 — start from the tenant default (falls back to 'window' pre-load /
        // for a non-entitled tenant, which the server also enforces).
        send_timing: (this.stoEntitled && this.stoDefault === 'optimise') ? 'optimise' : 'window',
      };
    },
    fmtDate(ts) { return ts ? new Date(ts * 1000).toLocaleString() : '—'; },
    // Insert a merge token at the subject input's cursor position (tracked via
    // selectionStart/selectionEnd, which survive the blur caused by clicking
    // the picker chip). Falls back to appending at the end if the input isn't
    // mounted or doesn't expose a selection (e.g. some mobile browsers).
    insertSubjectToken(token) {
      const input = this.$refs.subjectInput;
      const current = this.newCampaign.subject_override || '';
      const hasSelection = input && typeof input.selectionStart === 'number' && typeof input.selectionEnd === 'number';
      if (hasSelection) {
        const start = input.selectionStart;
        const end = input.selectionEnd;
        this.newCampaign.subject_override = current.slice(0, start) + token + current.slice(end);
        this.$nextTick(() => {
          const pos = start + token.length;
          input.focus();
          input.setSelectionRange(pos, pos);
        });
      } else {
        this.newCampaign.subject_override = current + token;
      }
    },
    // Highlight the leading arm for a metric — only when there's a clear,
    // non-zero leader (ties / all-zero aren't a "winner").
    isWinner(metric, v) {
      const vs = this.selected?.variants || [];
      if (vs.length < 2 || !v[metric]) return false;
      const max = Math.max(...vs.map(x => x[metric] || 0));
      return v[metric] === max && vs.filter(x => x[metric] === max).length === 1;
    },
    /** Significance readout for a metric ({leader, confidence, significant}). */
    sigLabel(s) {
      if (!s || !s.leader) return '<span class="text-muted">no difference yet</span>';
      const arm = esc(s.leader.toUpperCase());
      const conf = (s.confidence * 100).toFixed(1);
      return s.significant
        ? `<strong class="text-success">${arm} wins</strong> <span class="text-muted">(${conf}% conf)</span>`
        : `<span>${arm} leads</span> <span class="text-muted">(${conf}% conf — not significant)</span>`;
    },
    // Paused campaigns are RESUMED (re-queues held messages), not re-launched —
    // re-launching would re-resolve recipients and double-send.
    canLaunch(c) { return ['draft', 'scheduled'].includes(c.status); },
    /** datetime-local string → unix seconds (or null). */
    localToUnix(s) {
      if (!s) return null;
      const ms = new Date(s).getTime();
      return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
    },
    /** unix seconds → datetime-local string for the input (or ''). */
    unixToLocal(ts) {
      if (!ts) return '';
      const d = new Date(ts * 1000);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    toggleCreate() {
      if (this.showCreateForm) {
        this.showCreateForm = false;
        this.editingId = null;
        this.newCampaign = this.blankCampaign();
      } else {
        this.editingId = null;
        this.newCampaign = this.blankCampaign();
        this.showCreateForm = true;
      }
    },
    openEdit(c) {
      this.editingId = c.id;
      this.newCampaign = {
        name: c.name || '',
        description: c.description || '',
        template_id: c.template_id ?? null,
        template_b_id: c.template_b_id ?? null,
        sender_identity_id: c.sender_identity_id ?? null,
        subject_override: c.subject_override || '',
        subject_b: c.subject_b || '',
        list_id: c.list_id ?? null,
        segment_id: c.segment_id ?? null,
        scheduled_at_local: this.unixToLocal(c.scheduled_at),
        send_window: c.send_window || null,
        send_timing: c.send_timing === 'optimise' ? 'optimise' : 'window',
      };
      this.showCreateForm = true;
    },
    async refresh() {
      this.busy.load = true; this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/campaigns');
        const payload = res?.data?.data ?? res?.data ?? res;
        this.rows = Array.isArray(payload) ? payload : (payload?.rows || []);
      } catch (e) { this.error = e?.message || 'Failed to load campaigns.'; }
      this.busy.load = false;
    },
    async onRowClick(row) {
      if (!row?.id) return;
      try {
        const res = await this.api.get(`/api/v1/admin/campaigns/${row.id}`);
        const payload = res?.data ?? res;
        this.selected = { campaign: payload.campaign, stats: payload.stats || {}, variants: payload.variants || [], effective_window: payload.effective_window || null };
        this.pecr = null; this.showChecks = false;
        this.loadPecr(row.id);
        this.audience = null;
        if (this.canLaunch(payload.campaign || {})) this.loadAudience(row.id);
        this.lastLaunchMessage = '';
        // Reset per-campaign inline forms so stale input/feedback from a
        // previously selected campaign doesn't leak into this one.
        this.testSendEmail = '';
        this.testSendMessage = '';
        this.resendForm = { show: false, subject: '' };
        this.resendMessage = '';
      } catch (e) { this.error = e?.message || 'Failed to open campaign.'; }
    },
    async saveCampaign() {
      this.busy.create = true; this.error = null;
      try {
        const body = { ...this.newCampaign };
        // scheduled_at_local (datetime-local) → scheduled_at (unix). The server
        // re-derives draft↔scheduled from this value.
        body.scheduled_at = this.localToUnix(body.scheduled_at_local);
        delete body.scheduled_at_local;
        // W1 window spec → send_window_json (map key + carry through the strip
        // below so an edit can CLEAR the override by sending null).
        const sendWindow = body.send_window ?? null;
        delete body.send_window;
        // Strip blanks so 422 validation messages stay meaningful (keep
        // scheduled_at even when null so an edit can clear a schedule).
        Object.keys(body).forEach((k) => {
          if (k !== 'scheduled_at' && (body[k] === '' || body[k] === null)) delete body[k];
        });
        // Always send the window (null = inherit on create / clear on edit).
        body.send_window_json = sendWindow;

        let payload;
        if (this.editingId) {
          const res = await this.api.put(`/api/v1/admin/campaigns/${this.editingId}`, body);
          payload = res?.data ?? res;
        } else {
          const res = await this.api.post('/api/v1/admin/campaigns', body);
          payload = res?.data ?? res;
        }
        if (payload?.campaign?.id) {
          const id = payload.campaign.id;
          this.showCreateForm = false;
          this.editingId = null;
          this.newCampaign = this.blankCampaign();
          await this.refresh();
          await this.onRowClick({ id });
        } else {
          this.error = payload?.error || 'Save failed.';
        }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Save failed.';
      }
      this.busy.create = false;
    },
    async transition(id, action) {
      const verb = { pause: 'Pause', resume: 'Resume', cancel: 'Cancel' }[action] || action;
      if (action === 'cancel' && !confirm('Cancel this campaign? Anything not yet sent will be dropped — this cannot be undone.')) return;
      this.busy.transition = true; this.lastLaunchMessage = ''; this.launchOk = false;
      try {
        const res = await this.api.post(`/api/v1/admin/campaigns/${id}/${action}`, {});
        const payload = res?.data ?? res;
        if (payload?.success) {
          this.launchOk = true;
          this.lastLaunchMessage = `${verb} applied — status: ${payload.campaign_status || 'updated'}.`;
          await this.refresh();
          await this.onRowClick({ id });
        } else {
          this.lastLaunchMessage = payload?.error || `${verb} failed.`;
        }
      } catch (e) {
        this.lastLaunchMessage = e?.response?.data?.error || e?.message || `${verb} failed.`;
      }
      this.busy.transition = false;
    },
    async launch(id) {
      if (!confirm('Launch this campaign now? The recipient set will be resolved + every matching subscriber will get one queued email.')) return;
      this.busy.launch = true; this.lastLaunchMessage = ''; this.launchOk = false;
      try {
        const res = await this.api.post(`/api/v1/admin/campaigns/${id}/launch`, {});
        const payload = res?.data ?? res;
        if (payload?.success) {
          this.launchOk = true;
          const skips = payload.skipped > 0 ? `, ${payload.skipped} skipped (${Object.entries(payload.skip_reasons || {}).map(([k, v]) => `${k}: ${v}`).join(', ')})` : '';
          this.lastLaunchMessage = `Launched. Found ${payload.resolved} recipient${payload.resolved === 1 ? '' : 's'}, ${payload.queued} queued to send${skips}. Yorlin sends these automatically over the next few minutes.`;
          await this.refresh();
          await this.onRowClick({ id });
        } else {
          this.lastLaunchMessage = payload?.error || 'Launch failed.';
        }
      } catch (e) {
        const body = e?.response?.data;
        if (body?.error === 'compliance_blocked') {
          // Blocking mode stopped the send — show why + the failing checks.
          this.lastLaunchMessage = body.message || 'Send blocked by compliance checks.';
          if (body.pecr) { this.pecr = { ...body.pecr, badge_mode: 'blocking' }; this.showChecks = true; }
        } else {
          this.lastLaunchMessage = body?.error || e?.message || 'Launch failed.';
        }
      }
      this.busy.launch = false;
    },
    async deleteCampaign(id) {
      if (!confirm('Delete this draft? (Only drafts can be deleted; launched campaigns stay for audit.)')) return;
      try {
        await this.api.delete(`/api/v1/admin/campaigns/${id}`);
        this.selected = null;
        await this.refresh();
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Delete failed.';
      }
    },
    // Basic client-side shape check — the server is the real validator
    // (filter_var FILTER_VALIDATE_EMAIL).
    isValidEmail(v) { return !!v && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); },
    // Non-destructive preview send — no confirm() needed. Subject gets a
    // [TEST] prefix server-side; doesn't touch campaign status or audience.
    async sendTest() {
      if (!this.selected || !this.isValidEmail(this.testSendEmail)) return;
      const to = this.testSendEmail;
      this.busy.testSend = true; this.testSendMessage = ''; this.testSendOk = false;
      try {
        const res = await this.api.post(`/api/v1/admin/campaigns/${this.selected.campaign.id}/test-send`, { to });
        const payload = res?.data ?? res;
        if (payload?.success) {
          this.testSendOk = true;
          this.testSendMessage = `Test queued to ${to}.`;
        } else {
          this.testSendMessage = payload?.error || 'Test send failed.';
        }
      } catch (e) {
        this.testSendMessage = e?.response?.data?.error || e?.message || 'Test send failed.';
      }
      this.busy.testSend = false;
    },
    toggleResendForm() {
      this.resendForm.show = !this.resendForm.show;
      this.resendMessage = '';
    },
    // Queues a fresh send to everyone who never opened this campaign
    // (server enforces status sending/sent — mirrored in the button's v-if).
    async resendNonOpeners() {
      if (!this.selected) return;
      if (!confirm("Queue a resend to everyone who hasn't opened? This sends a fresh email to every non-opener on this campaign.")) return;
      const id = this.selected.campaign.id;
      this.busy.resend = true; this.resendMessage = ''; this.resendOk = false;
      try {
        const body = {};
        if (this.resendForm.subject) body.subject = this.resendForm.subject;
        const res = await this.api.post(`/api/v1/admin/campaigns/${id}/resend-non-openers`, body);
        const payload = res?.data ?? res;
        if (payload?.success) {
          const skips = payload.skipped > 0 ? `, ${payload.skipped} skipped` : '';
          const msg = `Resolved ${payload.resolved}, queued ${payload.queued}${skips}.`;
          this.resendForm = { show: false, subject: '' };
          // onRowClick resets the per-campaign inline-form state (including
          // resendMessage) when it re-fetches, so set the success message
          // *after* it resolves rather than before.
          await this.refresh();
          await this.onRowClick({ id });
          this.resendOk = true;
          this.resendMessage = msg;
        } else {
          this.resendMessage = payload?.error || 'Resend failed.';
        }
      } catch (e) {
        this.resendMessage = e?.response?.data?.error || e?.message || 'Resend failed.';
      }
      this.busy.resend = false;
    },
  },
};
</script>

<style>
.musubi-page--campaigns { padding: var(--m-space-6, 32px); }
.wizard-xlink {
  display: flex; align-items: center; gap: 10px;
  background: var(--m-accent-soft, #EEF0FF);
  border: 1px solid color-mix(in srgb, var(--m-accent, #4F46E5) 26%, #fff);
  border-radius: var(--m-radius, 8px);
  padding: 10px 14px; margin-bottom: 14px;
  font-size: 0.84rem; color: var(--m-text-secondary, #475569);
}
.wizard-xlink__ic { color: var(--m-accent, #4F46E5); flex: none; }
.wizard-xlink a { color: var(--m-accent, #4F46E5); font-weight: 650; text-decoration: none; }
.wizard-xlink a:hover { text-decoration: underline; }
.actions-bar { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.create-form { background: var(--m-bg-card, #FFF); }
.campaign-detail { background: var(--m-bg-card, #FFF); }
.stat-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 8px;
}
.stat-tile {
  background: var(--m-bg-card, #FFF);
  border: 1px solid var(--m-border, #E2E8F0);
  border-radius: var(--m-radius, 6px);
  padding: 10px 12px;
  border-left-width: 3px;
}
.stat-tile--wait   { border-left-color: var(--m-status-info, #3B82F6); }
.stat-tile--ok     { border-left-color: var(--m-status-success, #10B981); }
.stat-tile--warn   { border-left-color: var(--m-status-warning, #F59E0B); }
.stat-tile--error  { border-left-color: var(--m-status-danger, #EF4444); }
.stat-tile--engage { border-left-color: var(--m-accent, #8B5CF6); }
.stat-tile__value  { font-size: 1.15rem; font-weight: 600; line-height: 1; }
.stat-tile__label  { margin-top: 4px; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-secondary, #475569); }

.status-pill {
  display: inline-block;
  padding: 2px 8px;
  font-size: 0.72rem;
  font-weight: 500;
  border-radius: 100px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  vertical-align: middle;
  margin-left: 6px;
}
.ab-arm { display:inline-block; min-width:22px; text-align:center; padding:1px 7px; border-radius:100px; font-size:0.72rem; font-weight:600; background:#EEF2FF; color:#3730A3; }
.send-window-line { color: var(--m-text-secondary, #475569); margin: -4px 0 12px; }

/* Action row + inline add-on panels (resend / test-send) below the detail card */
.campaign-actions { border-color: var(--m-border, #E2E8F0); }
.inline-panel {
  background: var(--m-bg-subtle, #F8FAFC);
  border: 1px solid var(--m-border, #E2E8F0);
  border-radius: var(--m-radius, 8px);
  padding: 12px 14px;
}
.test-send-form__email { max-width: 260px; }
.resend-form__subject { max-width: 320px; }
.ab-sig__metric { font-weight:600; }
.ab-sig__sep { margin:0 8px; color:var(--m-text-muted,#94A3B8); }
.status-pill--draft     { background: #E5E7EB; color: #374151; }
.status-pill--scheduled { background: #DBEAFE; color: #1E40AF; }
.status-pill--sending   { background: #FEF3C7; color: #92400E; }
.status-pill--sent      { background: #DCFCE7; color: #166534; }
.status-pill--paused    { background: #F3E8FF; color: #6B21A8; }
.status-pill--cancelled { background: #FEE2E2; color: #991B1B; }
.status-pill--failed    { background: #FEE2E2; color: #991B1B; }

/* ── PECR-checked badge + checks (Growth Feature 5) ── */
.pecr-badge { display:inline-flex; align-items:center; gap:5px; font-size:.72rem; font-weight:800; padding:4px 10px; border-radius:999px; background:#E4F7F4; color:#0F766E; border:1px solid #B6EBE4; white-space:nowrap; }
.pecr-badge.fail { background:#FFF4E6; color:#9A5B12; border-color:#F0DCBB; }
.pecr-badge--sm { font-size:.64rem; padding:2px 8px; }
.audience-panel { border:1px solid var(--m-border,#EAECF3); border-radius:12px; padding:12px 16px; background:#FCFDFF; }
.audience-counts { list-style:none; padding:0; display:grid; gap:4px; }
.audience-counts li.is-skip { color:#9A5B12; }
.audience-counts__n { display:inline-block; min-width:2.5em; font-weight:800; text-align:right; margin-right:6px; }
.audience-counts__tag { margin-left:8px; font-size:.64rem; font-weight:800; text-transform:uppercase; letter-spacing:.06em; }
.pecr-panel { border:1px solid var(--m-border,#EAECF3); border-radius:12px; padding:14px 16px; margin-bottom:16px; background:#FCFDFF; }
.pecr-panel__head { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
.pecr-note { margin-top:8px; }
.pecr-checks { list-style:none; padding:0; margin:12px 0 0; display:grid; gap:8px; }
.pecr-checks li { display:flex; gap:10px; align-items:flex-start; padding:9px 11px; border:1px solid var(--m-border,#EAECF3); border-radius:9px; background:#fff; }
.pecr-checks li.is-fail { background:#FFFCF6; border-color:#F0DCBB; }
.pecr-checks li.is-gated { background:#FBFAFF; border-color:#E0D2FB; }
.chk__ic { width:20px; height:20px; border-radius:50%; flex:none; display:flex; align-items:center; justify-content:center; font-size:.66rem; margin-top:1px; }
.chk__ic.pass { background:#E4F7F4; color:#0F766E; }
.chk__ic.fail { background:#FFF4E6; color:#9A5B12; }
.chk__ic.gated { background:#F3EEFE; color:#7C3AED; }
.chk__body { display:flex; flex-direction:column; }
.chk__t { font-size:.84rem; font-weight:600; }
.chk__gated-tag { font-size:.58rem; font-weight:800; text-transform:uppercase; color:#7C3AED; background:#F3EEFE; border-radius:4px; padding:0 5px; margin-left:6px; }
.chk__m { font-size:.76rem; color:var(--m-text-secondary,#697086); margin-top:1px; line-height:1.4; }
.chk__fix { font-size:.76rem; font-weight:700; color:#4338CA; text-decoration:none; margin-top:4px; }
.chk__fix:hover { text-decoration:underline; }
</style>
